import { type Address } from "viem";

import {
  type PerpLimitOrder,
  type PostOptionLegMarket,
  type PostPerpLegMarket,
  type PostPerpsLimit,
  type PostPerpsLimitRequest,
} from "./api";
import { Pairs } from "./markets";
import { OrderbookEntry } from "./orderbook";
import { SignedRequestWithDeadline } from "./signatureRequest";
import { TradeDirection } from "./trade";

export enum OrderOptionType {
  Call = "C",
  Put = "P",
}

export interface OptionOrder {
  instrumentName: string;
  base: string;
  indexPrice: number;
  strike: number;
  ttm: number;
  type: OrderOptionType;
  interestRate: number;
  markIV: number;
}

export interface Perp {
  instrumentName: string;
  base: string;
  funding: number;
  indexPrice: number;
  isBuy: boolean;
  bids?: OrderbookEntry[];
  asks?: OrderbookEntry[];
}

// union types for perps and options
export type PostLimitOrder = PostOptionsLimit | PostPerpsLimit;
export type PostMarketOrder = PostOptionLegMarket | PostPerpLegMarket;
export type Order = PostLimitOrder | PostMarketOrder;

// signed + API metadata
export type PostLimitOrderRequest =
  | PostOptionsLimitRequest
  | PostPerpsLimitRequest;

// NOTE: matched & partially filled LO = fill response and remainder (original LO w/ filled_amount)
// fill response metadata is available in BQ
type PartiallyFilledLimitOrder = PostLimitOrderRequest & {
  filled_amount: number;
};

export type StoreLimitOrderRequest =
  | PostLimitOrderRequest
  | PartiallyFilledLimitOrder;

export enum MarketOrder {
  FOK = "fill_or_kill",
  IOC = "immediate_or_cancel",
}

export interface Trade {
  market_order: PostPerpLegMarket | PostOptionLegMarket;
  limit_price: number;
  type: MarketOrder;
  taker: Address;
}

export interface PostMarketOrderRequest {
  instrument_name: string;
  direction: TradeDirection;
  contracts?: number;
  amount?: number;
}

export interface ComboTrade {
  market_orders: PostMarketOrderRequest[];
  limit_total_net_premium: number;
  limit_perp_price?: number;
  type: MarketOrder.FOK;
  taker: Address;
}

export interface TradeRequest extends SignedRequestWithDeadline, Trade {}
export interface ComboTradeRequest
  extends SignedRequestWithDeadline, ComboTrade {}

export type Match = [PostLimitOrderRequest, StoredLimitOrder[]];
export type MarketFill = [PostMarketOrderRequest, StoredLimitOrder[]];

export interface Fill extends Omit<Trade, "market_order"> {
  fill: MarketFill;
  trade_id: string;
}

export interface ComboFill extends Omit<ComboTrade, "market_orders"> {
  fills: MarketFill[];
  trade_id: string;
}

export interface RFQFill extends Omit<
  ComboTrade,
  "market_orders" | "limit_net_premium" | "limit_perp_price"
> {
  fills: MarketFill[];
  trade_id: string;
}

// TODO: define
interface Fees {
  taker_fee: number;
}

export interface PostMarketOrderResponse {
  instrument_name: string;
  direction: TradeDirection;
  taker: Address;
  trade_id: string;
  filled_amount: number;
  filled_contracts: number;
  average_price?: number; // NOTE: if not provided then the order was not filled
  fees: Fees;
}

export interface PostMarketComboOrderResponse {
  options: (PostOptionLegMarket & { average_price: number })[];
  perp?: PostPerpLegMarket & { average_price: number };
  net_premium: number;
  taker: Address;
  trade_id: string;
  fees: Fees;
}

export interface TradeResponse {
  fillResult: PostMarketOrderResponse;
  filledOrders: StoredLimitOrder[];
}

export interface ComboTradeResponse {
  fillResult: PostMarketComboOrderResponse;
  filledOrders: StoredLimitOrder[];
}

export type PostRFQOrderResponse = Omit<PostMarketComboOrderResponse, "perp">;

export interface RFQTradeResponse {
  fillResult: PostRFQOrderResponse;
  filledOrders: StoredLimitOrder[];
}

// stored to the Redis orderbook, full metadata
export type StoredLimitOrder = OptionLimitOrder | PerpLimitOrder;

export interface RFQFillRequest extends SignedRequestWithDeadline {
  taker: Address;
  response_id: string;
}

export interface OpenOrder {
  isOption: boolean;
  instrument: string;
  maturity: string;
  size: number;
  direction: TradeDirection;
  limitPrice: number;
  filled: number;
  orderId: string;
  tif: LimitOrder;
  date: string;
  isLiquidated: boolean;
  subOpenOrders?: OpenOrder[];
  orderState?: OrderState;
}

export interface RejectedLimitOrder extends PostMarketOrderResponse {
  reason: string;
}

export interface PostLimitOrderResponse {
  posted: OptionLimitOrder[];
  rejected: RejectedLimitOrder[];
}

export enum LimitOrder {
  GTC = "good_til_cancelled",
  GTD = "good_til_day",
}

export interface PostOptionsLimit {
  instrument_name: string;
  type: LimitOrder;
  contracts: number;
  direction: TradeDirection;
  price: number;
  post_only: boolean;
  mmp: boolean;
  liquidation: boolean;
  maker: Address;
  taker?: Address;
}

export enum OrderState {
  Open = "open",
  Filled = "filled",
  Rejected = "rejected",
}

export interface PostOptionsLimitRequest
  extends PostOptionsLimit, SignedRequestWithDeadline {}

export interface OptionLimitOrder extends PostOptionsLimitRequest {
  taker: Address;
  order_id: string;
  order_state: OrderState;
  filled_amount: number;
  average_price?: number;
  iv?: number;
  creation_timestamp: number;
  chain_id: number;
}

export type OpenOrdersByPairs = Record<Pairs, OpenOrder[]>;

export interface RFQResponseRequest {
  user_id: string;
  fills:
    | [PostMarketOrderRequest, PostLimitOrderRequest][]
    | [PostMarketOrderRequest, PostOptionsLimit | PostPerpsLimit][];
  order_id: string;
  taker: string;
  maker: string;
}
